import { createContext, ReactNode, useCallback, useContext, useRef, useState } from 'react';
import { UserSummary } from '../../database/user';
import { useRequest, Request } from '../Request';


export interface IdentifiableCache<T> {
    get: (id: string) => T | undefined;
    list: () => T[];
    filter: (predicate: (obj: T) => boolean) => T[];
    put: (obj: T) => void;
    putMany: (objs: T[]) => void;
    update: (obj: Partial<T>) => void;
    remove: (id: string) => void;
    isFetched: (id: string) => boolean;
    markFetched: (id: string) => void;
    request: Request;
}

export function useIdentifiableCache<T>(
    keyGetter?: (item: Partial<T>) => string,
): IdentifiableCache<T> {
    const [objects, setObjects] = useState<Record<string, T>>({});
    const fetchedIds = useRef<Record<string, boolean>>({});
    const request = useRequest();

    const get = useCallback(
        (id: string) => {
            return objects[id];
        },
        [objects],
    );

    const list = useCallback(() => Object.values(objects), [objects]);

    const filter = useCallback(
        (predicate: (obj: T) => boolean) => {
            return Object.values(objects).filter(predicate);
        },
        [objects],
    );

    const put = useCallback(
        (obj: T) => {
            const id = keyGetter ? keyGetter(obj) : (obj as any).id;
            setObjects((objects) => ({
                ...objects,
                [id]: obj,
            }));
        },
        [setObjects, keyGetter],
    );

    const putMany = useCallback(
        (objs: T[]) => {
            const newMap = objs.reduce((map: Record<string, T>, obj: T) => {
                const id = keyGetter ? keyGetter(obj) : (obj as any).id;
                map[id] = obj;
                return map;
            }, {});
            setObjects((objects) => ({
                ...objects,
                ...newMap,
            }));
        },
        [setObjects, keyGetter],
    );

    const update = useCallback(
        (obj: Partial<T>) => {
            const id = keyGetter ? keyGetter(obj) : (obj as any).id;
            if (!id) {
                return;
            }

            setObjects((objects) => {
                if (!objects[id]) {
                    return objects;
                }
                return {
                    ...objects,
                    [id]: {
                        ...objects[id],
                        ...obj,
                    },
                };
            });
        },
        [setObjects, keyGetter],
    );

    const remove = useCallback(
        (id: string) => {
            setObjects((objects) => {
                const { [id]: removed, ...others } = objects;
                return others;
            });
        },
        [setObjects],
    );

    const isFetched = useCallback(
        (id: string) => {
            return fetchedIds.current[id] || false;
        },
        [fetchedIds],
    );

    const markFetched = useCallback(
        (id: string) => {
            fetchedIds.current = {
                ...fetchedIds.current,
                [id]: true,
            };
        },
        [fetchedIds],
    );

    return {
        get,
        list,
        filter,
        put,
        putMany,
        update,
        remove,
        isFetched,
        markFetched,
        request,
    };
}

/**
 * CacheContextType defines the type of the cache as available through CacheProvider
 */
interface CacheContextType {
    isLoading: boolean;
    setIsLoading: (arg: boolean) => void;
    userSummaries: IdentifiableCache<UserSummary>;

    imageBypass: number;
    setImageBypass: (v: number) => void;
}

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const CacheContext = createContext<CacheContextType>(null!);

/**
 * @returns The current CacheContextType value.
 */
export function useCache() {
    return useContext(CacheContext);
}

/**
 * CacheProvider provides access to cached data. It implements the CacheContextType interface.
 * @param param0 React props. The only used prop is children.
 * @returns A CacheContext.Provider wrapping the provided children.
 */
export function CacheProvider({ children }: { children: ReactNode }) {
    const [isLoading, setIsLoading] = useState(false);
    const [imageBypass, setImageBypass] = useState(Date.now());
    const userSummaries = useIdentifiableCache<UserSummary>((userSummary) => userSummary?.username || '');

    const value = {
        isLoading,
        setIsLoading,
        userSummaries,
        imageBypass,
        setImageBypass,
    };
    return <CacheContext.Provider value={value}>{children}</CacheContext.Provider>;
}
